import React from 'react';

import _ from 'lodash';
import moment from 'moment';
import { Parser } from 'json2csv';

import { Icon } from 'semantic-grab-ui';

import Toastr from 'utils/toastr-util';
import {
  PROJECT_COUNTRY_QUERY_KEY,
  PROJECT_MORE_FILTERS_QUERY_KEY,
  PROJECT_OWNERSHIP_QUERY_KEY,
  PROJECT_PAGE_QUERY_KEY,
  PROJECT_PAGE_SIZE_QUERY_KEY,
  PROJECT_STATUS_QUERY_KEY,
  PROJECT_SEARCH_QUERY_KEY,
  STATUS,
  PROJECT_ES_FIELDS,
  CAMPAIGN_ES_FIELDS,
  CAMPAIGN_QUERY_KEYS,
  CAMPAIGN_DEFAULT_PAGE_SIZE,
  PROJECT_DEFAULT_PAGE_SIZE,
  OBJECTIVES,
  AUDIENCE_TYPES,
  VERTICALS,
  BUDGET_SOURCES,
  COMMON_ES_FIELDS,
  CHANNEL_GROUPS,
  CHANNEL_GROUP_DISPLAY_NAMES,
  CHANNEL_KEYS,
  METRICS_FIELD_MAPPING,
} from 'omega-rtc/constants/ucd';
import { transformObject } from 'omega-rtc/views/trident-campaigns/metrics/utils';

const verticalCodes = {
  [VERTICALS.transport]: 'GT',
  [VERTICALS.food]: 'GF',
  [VERTICALS.express]: 'GE',
  [VERTICALS.mart]: 'GM',
  [VERTICALS.finance]: 'GF',
  [VERTICALS.oneGrab]: 'OG',
  [VERTICALS.challenges]: 'CH',
  [VERTICALS.gfb]: 'GB',
  [VERTICALS.kitchen]: 'KI',
  [VERTICALS.rentals]: 'RN',
  [VERTICALS.rewards]: 'RW',
  [VERTICALS.grabUnlimited]: 'GU',
  [VERTICALS.omnicommerce]: 'OC',
  [VERTICALS.digitalGoods]: 'DG',
  [VERTICALS.brand]: 'BR',
};

const audienceCodes = {
  [AUDIENCE_TYPES.pax]: 'P',
  [AUDIENCE_TYPES.dax]: 'D',
  [AUDIENCE_TYPES.mex]: 'M',
};

const objectiveCodes = {
  [OBJECTIVES.demandGeneration]: 'D',
  [OBJECTIVES.userAcquisition]: 'A',
  [OBJECTIVES.userRetention]: 'R',
  [OBJECTIVES.communications]: 'C',
  [OBJECTIVES.branding]: 'B',
  [OBJECTIVES.reward]: 'W',
  [OBJECTIVES.partnership]: 'P',
};

const budgetTypeCodes = {
  [BUDGET_SOURCES.transportAcquisition]: 'T1',
  [BUDGET_SOURCES.transportRetention]: 'T2',
  [BUDGET_SOURCES.foodAcquisition]: 'F1',
  [BUDGET_SOURCES.foodRetention]: 'F2',
  [BUDGET_SOURCES.oneGrabAcquisition]: 'O1',
  [BUDGET_SOURCES.oneGrabRetention]: 'O2',
  [BUDGET_SOURCES.paymentAcquisition]: 'P1',
  [BUDGET_SOURCES.paymentRetention]: 'P2',
  [BUDGET_SOURCES.martAcquisition]: 'M1',
  [BUDGET_SOURCES.martRetention]: 'M2',
  [BUDGET_SOURCES.express]: 'E1',
  [BUDGET_SOURCES.newBiz]: 'N1',
};

interface ProjectMeta {
  countryCode: string;
  year: number;
  audience: string;
  objective: string;
  shortName: string;
}

interface CampaignMeta {
  countryCode: string;
  year: number;
  audience: string;
  objective: string;
  shortName: string;
  vertical: string;
  budgetType: string;
}

// generateProjectCode generates a code based on:
// country, year, audience, objective, shortName
export function generateProjectCode(meta: ProjectMeta): string {
  const { countryCode, year, audience, objective, shortName } = meta;
  const audienceCode = _.get(audienceCodes, _.camelCase(audience), '');
  const objectiveCode = _.get(objectiveCodes, _.camelCase(objective), '');
  return `${_.toUpper(countryCode)}${
    year % 100
  }${audienceCode}${objectiveCode}${_.toUpper(shortName)}`;
}

// generateCampaignCode generates a code based on:
// country, year, vertical, audience, objective, budget, shortName
export function generateCampaignCode(meta: CampaignMeta): string {
  const {
    countryCode,
    year,
    audience,
    objective,
    shortName,
    vertical,
    budgetType,
  } = meta;
  const audienceCode = _.get(audienceCodes, _.camelCase(audience), '');
  const objectiveCode = _.get(objectiveCodes, _.camelCase(objective), '');
  const verticalCode = _.get(verticalCodes, _.camelCase(vertical), '');
  const budgetCode = _.get(budgetTypeCodes, _.camelCase(budgetType), '');
  return `${_.toUpper(countryCode)}${
    year % 100
  }${verticalCode}${audienceCode}${objectiveCode}${budgetCode}${_.toUpper(
    shortName,
  )}`;
}

// getPositiveNumber returns val if it's a positive number, other returns defaultNumber
export function getPositiveNumber(val: any, defaultNumber: number): number {
  if (!_.isNaN(Number(val)) && Number(val) > 0) {
    return Number(val);
  }
  return defaultNumber;
}

export const getCountryCodeFromCountryID = (countryID, countries) => {
  for (const countryObj of countries) {
    if (countryObj.countryID === countryID) {
      return countryObj.code;
    }
  }
};

export const getCountryTimezonesFromCountryID = (countryID, countries) => {
  for (const countryObj of countries) {
    if (countryObj.countryID === countryID) {
      return countryObj.timezones;
    }
  }
};

export const getCountryNameFromCountryID = (countryID, countries) => {
  for (const countryObj of countries) {
    if (countryObj.countryID === countryID) {
      return countryObj.name;
    }
  }
};

export const getCountryOptions = (countries) => {
  return countries.map((country) => {
    return {
      label: country.name,
      value: country.countryID,
    };
  });
};

export const getObjectValue = (obj, key) => {
  const value = _.get(obj, key);
  return value ? JSON.parse(value) : value;
};

interface Filter {
  lhs: string;
  operator: string;
  rhs: any;
}

// getFiltersForStatus returns a list of filter objects
function getFiltersForStatus(status: string): Filter[] {
  status = _.toUpper(status);
  const internalStatus = [
    STATUS.draft,
    STATUS.deactivated,
    STATUS.pendingCreation,
    STATUS.pendingApproval,
    STATUS.pendingAIP,
  ].includes(status)
    ? status
    : 'ACTIVE';
  const filters = [
    {
      lhs: PROJECT_ES_FIELDS.status,
      operator: 'eq',
      rhs: internalStatus,
    },
  ];

  const today = moment().format('YYYY-MM-DD');
  // add filters for  date
  if (status === STATUS.upcoming) {
    filters.push({
      lhs: PROJECT_ES_FIELDS.start,
      operator: '!lte',
      rhs: today,
    });
  } else if (status === STATUS.running) {
    filters.push({
      lhs: PROJECT_ES_FIELDS.start,
      operator: 'lte',
      rhs: today,
    });
    filters.push({
      lhs: PROJECT_ES_FIELDS.end,
      operator: 'gte',
      rhs: today,
    });
  } else if (status === STATUS.completed) {
    filters.push({
      lhs: PROJECT_ES_FIELDS.end,
      operator: '!gte',
      rhs: today,
    });
  }

  return filters;
}

interface SearchRequest {
  filters: Filter[];
  sort: {
    field: string;
    asc: boolean;
  };
  page?: number;
  pageSize?: number;
}

export function generateSearchProjectRequestBody(
  params: Record<string, any>,
  user: string,
  sortField: string,
): SearchRequest {
  // if text search is used, we should sort by score,
  // otherwise we should sort by updated time
  const defaultSortField = _.isEmpty(_.get(params, PROJECT_SEARCH_QUERY_KEY))
    ? COMMON_ES_FIELDS.updatedAt
    : COMMON_ES_FIELDS.score;
  const request = {
    filters: [],
    sort: {
      field: _.isEmpty(sortField) ? defaultSortField : sortField,
      asc: false,
    },
  };
  request.page = Number(_.get(params, PROJECT_PAGE_QUERY_KEY, 1));
  request.pageSize = Number(
    _.get(params, PROJECT_PAGE_SIZE_QUERY_KEY, PROJECT_DEFAULT_PAGE_SIZE),
  );
  if (PROJECT_COUNTRY_QUERY_KEY in params) {
    request.filters.push({
      lhs: PROJECT_ES_FIELDS.countryID,
      operator: 'in',
      rhs: getObjectValue(params, PROJECT_COUNTRY_QUERY_KEY),
    });
  }
  if (_.get(params, PROJECT_OWNERSHIP_QUERY_KEY) === 'true') {
    request.filters.push({
      lhs: PROJECT_ES_FIELDS.creator,
      operator: 'eq',
      rhs: user,
    });
  }
  if (PROJECT_STATUS_QUERY_KEY in params) {
    const status = _.get(params, PROJECT_STATUS_QUERY_KEY);
    if (!_.isEmpty(status)) {
      request.filters = [...request.filters, ...getFiltersForStatus(status)];
    }
  }
  if (PROJECT_SEARCH_QUERY_KEY in params) {
    request.filters.push({
      lhs: PROJECT_ES_FIELDS.name,
      operator: 'match',
      rhs: _.get(params, PROJECT_SEARCH_QUERY_KEY),
    });
  }
  if (PROJECT_MORE_FILTERS_QUERY_KEY in params) {
    const moreFilters = getMoreFilters(params, PROJECT_MORE_FILTERS_QUERY_KEY);
    request.filters = [...request.filters, ...moreFilters];
  }
  return request;
}

export function generateSearchCampaignRequestBody(
  params: Record<string, any>,
  user: string,
  projectID: string,
  sortField?: string,
): SearchRequest {
  const isPendingApproval = _.get(params, CAMPAIGN_QUERY_KEYS.pendingApproval) === 'true';

  // if text search is used, we should sort by score,
  // if pending approval, sort by start date ASC (most urgent first),
  // otherwise we should sort by updated time
  let defaultSortField = COMMON_ES_FIELDS.updatedAt;
  let defaultSortAsc = false;

  if (!_.isEmpty(_.get(params, CAMPAIGN_QUERY_KEYS.search))) {
    defaultSortField = COMMON_ES_FIELDS.score;
  } else if (isPendingApproval) {
    defaultSortField = CAMPAIGN_ES_FIELDS.start;
    defaultSortAsc = true;
  }

  const request = {
    filters: [],
    sort: {
      field: _.isEmpty(sortField) ? defaultSortField : sortField,
      asc: defaultSortAsc,
    },
    page: getPositiveNumber(_.get(params, CAMPAIGN_QUERY_KEYS.page), 1),
    pageSize: getPositiveNumber(
      _.get(params, CAMPAIGN_QUERY_KEYS.pageSize, CAMPAIGN_DEFAULT_PAGE_SIZE),
    ),
  };

  if (CAMPAIGN_QUERY_KEYS.country in params) {
    request.filters.push({
      lhs: CAMPAIGN_ES_FIELDS.countryID,
      operator: 'in',
      rhs: getObjectValue(params, CAMPAIGN_QUERY_KEYS.country),
    });
  }
  if (CAMPAIGN_QUERY_KEYS.vertical in params) {
    request.filters.push({
      lhs: CAMPAIGN_ES_FIELDS.verticals,
      operator: 'in',
      rhs: getObjectValue(params, CAMPAIGN_QUERY_KEYS.vertical),
    });
  }
  if (_.get(params, CAMPAIGN_QUERY_KEYS.ownership) === 'true') {
    request.filters.push({
      lhs: CAMPAIGN_ES_FIELDS.creator,
      operator: 'eq',
      rhs: user,
    });
  }
  if (isPendingApproval) {
    request.filters.push({
      lhs: CAMPAIGN_ES_FIELDS.status,
      operator: 'eq',
      rhs: 'PENDING',
    });
    request.filters.push({
      lhs: CAMPAIGN_ES_FIELDS.approver,
      operator: 'eq',
      rhs: user,
    });
    // Only show campaigns starting tomorrow or later
    const tomorrow = moment().add(1, 'day').startOf('day').format('YYYY-MM-DD');
    request.filters.push({
      lhs: CAMPAIGN_ES_FIELDS.start,
      operator: 'gte',
      rhs: tomorrow,
    });
  } else if (CAMPAIGN_QUERY_KEYS.status in params) {
    const status = _.get(params, CAMPAIGN_QUERY_KEYS.status);
    if (!_.isEmpty(status)) {
      request.filters = [...request.filters, ...getFiltersForStatus(status)];
    }
  }
  if (CAMPAIGN_QUERY_KEYS.moreFilters in params) {
    const moreFilters = getMoreFilters(params, CAMPAIGN_QUERY_KEYS.moreFilters);
    request.filters = [...request.filters, ...moreFilters];
  }
  if (CAMPAIGN_QUERY_KEYS.search in params) {
    request.filters.push({
      lhs: `${CAMPAIGN_ES_FIELDS.name},${CAMPAIGN_ES_FIELDS.code}`,
      operator: 'multiMatch',
      rhs: _.get(params, CAMPAIGN_QUERY_KEYS.search),
    });
  }
  if (!_.isEmpty(projectID)) {
    request.filters.push({
      lhs: CAMPAIGN_ES_FIELDS.projectID,
      operator: 'eq',
      rhs: projectID,
    });
  }

  return request;
}

function getMoreFilters(params: Record<string, any>, key: string): Filter[] {
  const moreFilters = getObjectValue(params, key);
  if (_.isEmpty(moreFilters)) {
    return [];
  }
  const dateFields = [
    CAMPAIGN_ES_FIELDS.start,
    CAMPAIGN_ES_FIELDS.end,
    PROJECT_ES_FIELDS.start,
    PROJECT_ES_FIELDS.end,
  ];
  for (let i = 0; i < moreFilters.length; i += 1) {
    if (_.includes(dateFields, moreFilters[i].lhs)) {
      moreFilters[i].rhs = moment(moreFilters[i].rhs).format('YYYY-MM-DD');
    }
  }
  return moreFilters;
}

const operators = ['gte', 'lte', 'eq'];
interface DateInitialValue {
  operator: string;
  date?: any;
}

export const getDateInitialValue = (
  requestKey: string,
  moreFiltersInitialValue: Filter[],
): DateInitialValue => {
  const initialValue = { operator: 'gte' };
  const filterObj = _.find(moreFiltersInitialValue, (v) => {
    return v && v.lhs === requestKey;
  });
  // This is a dummy commit
  if (filterObj) {
    if (filterObj.operator && operators.indexOf(filterObj.operator) !== -1) {
      initialValue.operator = filterObj.operator;
    }
    if (filterObj.rhs) {
      initialValue.date = moment(filterObj.rhs);
    }
  }
  return initialValue;
};

export const getArrayInitialValue = (field: string, filters: Filter[]): any[] => {
  return getFilterValue(filters, field, []);
};

export const getStringInitialValue = (field: string, filters: Filter[]): string => {
  return getFilterValue(filters, field, '');
};

// getFilterValue finds the filter for `field` in the `filters` array, and return the filter's rhs
export const getFilterValue = (
  filters: Filter[],
  field: string,
  defaultVal: any,
): any => {
  const filter = _.find(filters, (v) => {
    return v && v.lhs === field;
  });
  return _.get(filter, 'rhs', defaultVal);
};

export function copyToClipboard(text) {
  const el = document.createElement('textarea');
  el.value = text;
  document.body.appendChild(el);
  el.select();
  document.execCommand('copy');
  document.body.removeChild(el);
}

export function onCreateProjectHelper(
  form: any,
  countries: any[],
  user: any,
  createProjectCallback: (data: any) => void,
  status: string,
): void {
  form.validateFields((err, values) => {
    if (!err) {
      const { audience, country, goal, projectCode, projectName, subGoal } =
        values;
      let generatedCode = '';

      if (country && audience && goal && projectCode) {
        generatedCode = generateProjectCode({
          countryCode: _.find(countries, ['countryID', values.country]).code,
          year: new Date().getFullYear(),
          audience,
          objective: goal,
          shortName: projectCode,
        });
      }

      const chosenCountry = countries.find(
        (country) => country.countryID === values.country,
      );

      const data = {
        project: {
          name: projectName,
          shortName: projectCode,
          code: generatedCode,
          countryID: country,
          timezone: chosenCountry.timezones[0].tz,
          objective: goal,
          audience,
          subObjective: subGoal,
        },
        status,
        user: user.email,
      };
      createProjectCallback(data);
    } else {
      Toastr.error('Error occurred');
    }
  });
}

export const updateSearchView = (
  searchParams,
  queryKey,
  queryValue,
  router,
) => {
  const paramsObj = new URLSearchParams(searchParams);
  if (_.isObject(queryValue)) {
    if (_.isEmpty(queryValue)) {
      paramsObj.delete(queryKey);
    } else {
      paramsObj.set(queryKey, JSON.stringify(queryValue));
    }
  } else if (queryValue) {
    paramsObj.set(queryKey, queryValue);
  } else {
    paramsObj.delete(queryKey);
  }

  // Below section of code is to reset the page when corresponding project or campaign filter changes
  if (
    queryKey !== CAMPAIGN_QUERY_KEYS.page &&
    queryKey !== PROJECT_PAGE_QUERY_KEY
  ) {
    if (_.startsWith(queryKey, 'project_')) {
      if (paramsObj.has(PROJECT_PAGE_QUERY_KEY)) {
        paramsObj.delete(PROJECT_PAGE_QUERY_KEY);
      }
    } else if (_.startsWith(queryKey, 'campaign_')) {
      if (paramsObj.has(CAMPAIGN_QUERY_KEYS.page)) {
        paramsObj.delete(CAMPAIGN_QUERY_KEYS.page);
      }
    }
  }
  router.push(`${window.location.pathname}?${paramsObj.toString()}`);
};

export const getRequestStateKey = (action: string) =>
  `${_.camelCase(action)}Request`;

const channelGroupURLConfigs = {
  [CHANNEL_GROUPS.trident]: {
    baseURLKey: 'OMEGA_RTC_URI',
    pathRegExp: new RegExp('^/campaigns/[0-9]+/([a-z0-9]+)/[a-z]+'),
    extract: (__, pathMatch) => {
      return pathMatch[1];
    },
  },
  [CHANNEL_GROUPS.hedwig]: {
    baseURLKey: 'HEDWIG_DASH_URI',
    pathRegExp: new RegExp('^/templates/create/inApp/'),
    extract: (url) => {
      return url.searchParams.get('id');
    },
  },
  [CHANNEL_GROUPS.feed]: {
    baseURLKey: 'FEED_URI',
    pathRegExp: new RegExp(
      '^/cx/feedex/card/(\\w{3}-\\w{8}-\\w{4}-\\w{4}-\\w{4}-\\w{12})',
    ),
    extract: (__, pathMatch) => {
      return pathMatch[1];
    },
  },
};

const getBaseHostname = (baseURL: string) => {
  if (_.startsWith(baseURL, 'http:') || _.startsWith(baseURL, 'https:')) {
    const url = new URL(baseURL);
    return _.get(url, 'hostname');
  }
  return baseURL;
};

// extractCampaignIDFromURL returns an object {error, campaignID}
// here we assume urlStr is not empty
export const extractCampaignIDFromURL = (
  urlStr: string,
  channelGroup: string,
) => {
  let url;
  try {
    url = new URL(urlStr);
  } catch (_) {
    return { error: 'campaign link is an invalid url' };
  }

  // for hedwig, trident, feed campaigns
  const urlConfig = channelGroupURLConfigs[channelGroup];
  if (urlConfig) {
    if (
      url.hostname !==
      getBaseHostname(_.get(window.Grab.config, urlConfig.baseURLKey, ''))
    ) {
      return {
        error: `campaign link is not a ${CHANNEL_GROUP_DISPLAY_NAMES[channelGroup]} url`,
      };
    }

    const match = urlConfig.pathRegExp.exec(url.pathname);
    if (!match) {
      return { error: 'campaign link does not link to a campaign page' };
    }

    const campaignID = urlConfig.extract(url, match);
    if (_.isEmpty(campaignID)) {
      return { error: 'campaign ID is missing in the campaign link' };
    }
    return { campaignID };
  }

  // TODO: campaignID extraction for performance-marketing and salesforce

  return { campaignID: '' };
};

export const downloadCsv = (filename = 'download', data, fields) => {
  try {
    const parser = new Parser({ fields });
    const csv = parser.parse(data);
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.setAttribute('href', url);
    a.setAttribute('download', `${filename}.csv`);
    a.click();
  } catch (err) {
    alert('Cannot generate csv file');
  }
};

export const parseUCDSyncErrMsg = (ucdErrMessage) => {
  let reason = ucdErrMessage;
  const reasonArr = reason.split(',');
  if (reasonArr[2]) {
    const reasonMsg = reasonArr[2].split('=');
    if (reasonMsg[1]) reason = reasonMsg[1];
  }
  return reason;
};

export const loweredObj = (data) => {
  return _.transform(data, (result, val, key) => {
    result[key.toLowerCase()] = val;
  });
};

export const getTimezoneNameFromAbbrv = (
  timezoneAbbrv: string,
  countryTimezones: any[],
): string => {
  if (_.isEmpty(countryTimezones)) {
    return '';
  }

  const timezone = countryTimezones.find(
    (timezone) => timezone.code === timezoneAbbrv,
  );
  return _.get(timezone, 'tz', '');
};

export const getActionErrorDisplay = (errors: string[]): JSX.Element => {
  return (
    <div>
      <span style={{ color: 'red' }}> sub-action contains errors </span>
      <Icon name="hand point left outline" color="red" />
      <div
        style={{
          boxShadow:
            '0px 0px 0px 1px #e0b4b4 inset, 0px 0px 0px 0px rgb(0 0 0 / 0%)',
          padding: '12px 18px',
          width: 'fit-content',
          margin: '12px 0px',
          backgroundColor: '#FFF6F6',
          borderRadius: '5px',
          fontWeight: 700,
        }}
      >
        {errors.map((error, index) => {
          let errMsg = error
            .replace(/Nodes [0-9]*.data [0-9]*.meta/, '')
            .trim();
          if (errMsg && errMsg.length > 2) {
            errMsg = errMsg.charAt(0).toUpperCase() + errMsg.slice(1);
          }
          return (
            <div key={index} style={{ color: '#912D2B' }}>
              {errMsg}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Input: {firstName:"John", lastName : null}, Output: {firstName:"John"}
export const removeNullFields = (inputObject: Record<string, any>): Record<string, any> => {
  const cleanedObject = _.cloneDeep(inputObject);

  // Recursively remove null and undefined fields using omitBy
  _.forEach(cleanedObject, (value, key) => {
    if (_.isPlainObject(value) || _.isArray(value)) {
      cleanedObject[key] = _.omitBy(value, _.isNil);
      cleanedObject[key] = removeNullFields(cleanedObject[key]);
    }
  });

  return cleanedObject;
};

export const getTimeRanges = (startTime: string, frequency: string) => {
  let addedTime;
  switch (frequency) {
    case 'da':
    case 'nr':
      addedTime = 'days';
      break;
    case 'wk':
      addedTime = 'weeks';
      break;
    case 'mt':
      addedTime = 'months';
      break;
    default:
      return 'Invalid frequency';
  }

  const parsedStartTime = moment(startTime);
  const timePeriods = [];

  for (let i = 0; i < 3; i += 1) {
    const period = {
      startTime: parsedStartTime.clone(),
      endTime: parsedStartTime.clone().add(1, addedTime),
    };

    timePeriods.push(period);
    parsedStartTime.add(1, addedTime);
  }

  return timePeriods;
};

export const formatCampaignMetricsData = (
  campaignMetricsViewByDate: object,
  tridentCampaignView: object,
) => {
  const formattedCampaignMetricViewsByDate = {};
  let seenIdx = 1;
  const seenKeys = {};
  _.mapValues(campaignMetricsViewByDate, (item, dateRange) => {
    _.mapValues(item.comms, (value, key) => {
      if (!(key in seenKeys)) {
        seenKeys[key] = seenIdx;
        seenIdx += 1;
      }
      const headerName = `Communications #${seenKeys[key]}`;
      if (!formattedCampaignMetricViewsByDate[headerName]) {
        formattedCampaignMetricViewsByDate[headerName] = {
          comms: {},
          templateContent: {},
          type: '',
        };
      }
      formattedCampaignMetricViewsByDate[headerName].templateContent = {
        type: _.get(
          tridentCampaignView,
          `campaign.nodes[${key}].data.type`,
          '',
        ),
        ..._.get(
          tridentCampaignView,
          `campaign.nodes[${key}].data.meta.messagingPayload`,
          {},
        ),
      };
      formattedCampaignMetricViewsByDate[headerName].comms[dateRange] =
        constructStatsTableData(value);
      formattedCampaignMetricViewsByDate[headerName].summary = item.summary;
    });
  });
  return formattedCampaignMetricViewsByDate;
};

// Output: { "Email" : {"triggered": 100, "sent": 250, ...},  "PCI - Push" : {"triggered": 100, "sent": 250, ...}, ...}
export const constructStatsTableData = (commsMetrics) => {
  const channelToStatsMap = {};
  _.forEach(commsMetrics, (channelData, channelName) => {
    // channelData: {"triggered": 100, "sent": 250, ...}
    if (channelName === CHANNEL_KEYS.pci) {
      channelToStatsMap[CHANNEL_KEYS.pciPush] = transformObject(
        channelData,
        METRICS_FIELD_MAPPING.pci.push,
      );
      channelToStatsMap[CHANNEL_KEYS.pciInbox] = transformObject(
        channelData,
        METRICS_FIELD_MAPPING.pci.inbox,
      );
    } else {
      channelToStatsMap[channelName] = channelData;
    }
  });
  return channelToStatsMap;
};

export const constructChartProps = (summaryMetrics) => {
  const triggeredCount = _.get(
    summaryMetrics,
    METRICS_FIELD_MAPPING.default.triggered,
    0,
  );
  const sentCount = _.get(
    summaryMetrics,
    METRICS_FIELD_MAPPING.default.sent,
    0,
  );
  const deliveredCount = _.get(
    summaryMetrics,
    METRICS_FIELD_MAPPING.default.delivered,
    0,
  );
  const deliverabilityRate =
    sentCount !== 0 ? (deliveredCount / sentCount) * 100 : 0;
  return {
    lhs: [
      {
        name: 'Triggered',
        value: triggeredCount,
        description: 'This is non-unique user count',
      },
      {
        name: 'Sent',
        value: sentCount,
      },
      {
        name: 'Delivered',
        value: deliveredCount,
      },
    ],
    rhs: {
      name: 'Deliverability Rate',
      value: deliverabilityRate,
      description: 'Percentage of delivered message from total sent',
    },
    width: 200,
    height: 200,
    barColor: '#00B14F',
    shadowColor: '#E5FAEC',
    barDiffColor: '#f5f5f5',
  };
};

export const getSmartCommsRoundNameFromNumber = (roundNumber: number) => {
  return `round-${roundNumber}`;
};

export const getSmartCommsVariantNameFromNumber = (roundNumber: number) => {
  return `variant-${roundNumber}`;
};

export const getRoundNumberFromSmartCommsRoundName = (roundName: string) => {
  return Number(roundName.split('-')[1]);
};

export const getVariantNumberFromSmartCommsVariantName = (
  variantName: string,
) => {
  return Number(variantName.split('-')[1]);
};
